import React, { useState, useCallback, useEffect } from 'react';
import { Fingerprint, Loader2 } from 'lucide-react';
import { startAuthentication } from '@simplewebauthn/browser';
import { useAuth } from './AuthProvider';
import MfaChallengePage from './MfaChallengePage';
import { stashReturnUrlIfAuthHandoff, currentAuthHandoffReturnUrl } from './authHandoffReturn';
import axios from 'axios';
import { passkeysApi } from '../api/services';
import BrandMark from '../components/BrandMark';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:7001/api';

// Grain texture — an SVG `feTurbulence` fractalNoise overlay per spec,
// encoded as a data-URI background. Bypasses needing the SVG inline so the
// asset doesn't take up a top-level component slot. mix-blend-mode: overlay
// gives the dark canvas a subtle film-grain quality.
const grainSvg =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'>` +
      `<filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter>` +
      `<rect width='100%' height='100%' filter='url(#n)' opacity='1'/>` +
      `</svg>`,
  );

interface SsoCheckResult {
  ssoEnabled: boolean;
  provider?: string;
  displayName?: string;
  loginUrl?: string;
  enforceSso?: boolean;
  error?: string;
}

// Field tokens — aligned with the redesign Login.html spec. Dark cinematic
// surface, near-imperceptible field bg, hairline border, generous 11px
// radius, and a cool-blue 3px focus ring + #7DB1FF border (matching Signup
// and the Brief's focus accent).
const FIELD_CLASS =
  'h-12 rounded-[11px] border border-[rgba(234,238,246,0.12)] bg-[rgba(234,238,246,0.04)] px-[15px] py-[13px] text-[15px] text-[#EAEEF6] placeholder:text-[rgba(234,238,246,0.30)] focus-visible:border-[#7DB1FF] focus-visible:ring-[3px] focus-visible:ring-[rgba(125,177,255,0.16)]';

// Primary submit — near-white button on dark, ink-dark label, blue-glow
// hover. Matches the Signup primary button so the auth pair reads as one.
const PRIMARY_BUTTON =
  'h-12 w-full rounded-[11px] border-0 bg-[#EAEEF6] text-[15px] font-semibold text-[#070A12] transition-transform duration-150 hover:-translate-y-px hover:bg-[#EAEEF6] hover:shadow-[0_10px_32px_rgba(125,177,255,0.22)] disabled:opacity-60 disabled:hover:translate-y-0';

// Inline SSO (Continue with Microsoft) — same near-white treatment so it
// reads as an alternative submit, not a secondary action.
const SSO_BUTTON = PRIMARY_BUTTON;

const OAUTH_BUTTON =
  'h-11 w-full rounded-[11px] border border-[rgba(234,238,246,0.12)] bg-transparent text-[#EAEEF6] normal-case hover:border-[rgba(234,238,246,0.25)] hover:bg-[rgba(234,238,246,0.04)]';

const PASSKEY_BUTTON =
  'h-11 w-full rounded-[11px] border border-[rgba(125,177,255,0.30)] bg-transparent text-[#7DB1FF] normal-case hover:border-[#7DB1FF] hover:bg-[rgba(125,177,255,0.08)]';

function Divider({ label }: { label: string }) {
  return (
    <div className="my-3 flex items-center gap-2">
      <div className="h-px flex-1 bg-white/[0.08]" />
      <span className="text-xs text-[rgba(136,146,168,0.6)]">{label}</span>
      <div className="h-px flex-1 bg-white/[0.08]" />
    </div>
  );
}

// Friendly copy for the ?error= code the backend appends when a social /
// SSO sign-in can't complete — its callback redirects back to the app root
// with that code. The login page previously ignored the param entirely, so
// a bounced user just saw the form again with no explanation.
const OAUTH_ERROR_MESSAGES: Record<string, string> = {
  no_account:
    'No Cadence account matches that email address. Social sign-in works only for an existing account — ask your administrator for an invitation, or sign in with the email your account uses. (If you chose “Hide My Email”, Apple sends a private relay address that won’t match an account.)',
  no_email:
    'That provider didn’t share an email address, so we couldn’t match your account. Please try another sign-in method.',
  auth_failed: 'Sign-in with that provider didn’t complete. Please try again.',
  invalid_state:
    'Your sign-in session couldn’t be verified (it may have expired). Please try again.',
  apple_unavailable:
    'Sign in with Apple isn’t available right now. Please try another method.',
  google_unavailable:
    'Sign in with Google isn’t available right now. Please try another method.',
  microsoft_unavailable:
    'Sign in with Microsoft isn’t available right now. Please try another method.',
};

export default function LoginPage() {
  const { login, requiresMfa, applyTokens } = useAuth();

  // Document title — set per spec so the browser tab / window title
  // reflects the surface. Cheap one-time effect.
  useEffect(() => {
    const prev = document.title;
    document.title = 'Sign in · Cadence';
    return () => { document.title = prev; };
  }, []);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  /// Shown when the backend says the user must reset password but didn't
  /// supply a token (new-tenant signups where the welcome email carries
  /// the real reset link). Rendered as an info pill below the form, not
  /// as an error. v2.2.5.
  const [checkEmailMessage, setCheckEmailMessage] = useState('');

  const [ssoChecked, setSsoChecked] = useState(false);
  const [ssoChecking, setSsoChecking] = useState(false);
  const [ssoResult, setSsoResult] = useState<SsoCheckResult | null>(null);

  const [passkeyLoading, setPasskeyLoading] = useState(false);

  const [showMagicLink, setShowMagicLink] = useState(false);
  const [magicLinkEmail, setMagicLinkEmail] = useState('');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);

  // Entrance — `ready` flips once the page is visible so each `.rise`
  // element settles up from its initial state on its own per-slot
  // delay. Mirrors the Brief surface's triple-redundant trigger
  // (120ms timer + window load + 200ms fallback + visibilitychange)
  // so the reveal fires even if React paints late or the user
  // switches away/back before it finishes.
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const reveal = () => { if (!cancelled) setReady(true); };
    const t1 = window.setTimeout(reveal, 120);
    const t2 = window.setTimeout(reveal, 200);
    window.addEventListener('load', reveal);
    const onVis = () => { if (document.visibilityState === 'visible') reveal(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.removeEventListener('load', reveal);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  // Surface the error code from an OAuth / SSO callback redirect. It can
  // arrive two ways: (1) directly on the app root as ?error=… (the social
  // sign-in path, e.g. ?error=no_account), or (2) stashed in sessionStorage
  // as `authError` by the /auth/callback component, which consumes the query
  // itself and then bounces to a clean "/". Read BOTH — previously the URL
  // param was swallowed and the sessionStorage value was never read at all,
  // so a bounced user got no explanation either way. Then clear the source so
  // a refresh doesn't re-show it.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('error');
    const fromStash = sessionStorage.getItem('authError');
    const code = fromUrl || fromStash;
    if (!code) return;
    setError(OAUTH_ERROR_MESSAGES[code] || 'Sign-in failed. Please try again.');
    if (fromStash) sessionStorage.removeItem('authError');
    if (fromUrl) {
      params.delete('error');
      const qs = params.toString();
      const cleaned = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
      window.history.replaceState({}, '', cleaned);
    }
  }, []);

  // v2.2.3 — WebAuthn conditional UI. Kicks off a passkey assertion in
  // "autofill" mode on mount so password managers (NordPass, 1Password,
  // iCloud Keychain, Bitwarden, etc.) can surface the user's Cadence
  // passkey as an autocomplete suggestion when they focus the email
  // field — no button click required. The email input also gains
  // `autocomplete="username webauthn"` to associate the discovery with
  // that field. User-reported 2026-06-03: NordPass wasn't showing the
  // passkey for selection. Cause: we only ever called startAuthentication
  // as a button handler (explicit modal flow), never with the conditional
  // mediation that turns it into a discoverable autocomplete option.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (typeof PublicKeyCredential === 'undefined' ||
            typeof (PublicKeyCredential as any).isConditionalMediationAvailable !== 'function') {
          return;
        }
        const available = await (PublicKeyCredential as any).isConditionalMediationAvailable();
        if (!available || cancelled) return;

        const options = await passkeysApi.loginOptions();
        const challengeKey = options.challenge;
        const assertion = await startAuthentication({
          optionsJSON: options,
          useBrowserAutofill: true,
        });
        if (cancelled) return;
        const result = await passkeysApi.login({ response: assertion, challengeKey });
        setLeaving(true);
        await new Promise((r) => setTimeout(r, 250));
        applyTokens(result);
        const onAuthHandoffFlow =
          window.location.pathname.startsWith('/oauth/authorize')
          || window.location.pathname.startsWith('/oauth/desktop');
        if (!onAuthHandoffFlow && window.location.pathname !== '/') {
          window.history.replaceState(null, '', '/');
        }
      } catch {
        // Conditional UI cancellations are silent by design — the user
        // either chose not to use the suggested passkey or no passkey
        // matched. Don't surface errors here; the explicit "Sign in with
        // passkey" button (handlePasskeyLogin) does show errors when
        // intentionally clicked.
      }
    })();
    return () => { cancelled = true; };
    // applyTokens comes from useAuth() and is stable for the component
    // lifetime; safe to omit. We don't want this effect to re-run on
    // every render — the conditional UI session starts once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkSso = useCallback(async (emailValue: string) => {
    if (!emailValue || !emailValue.includes('@')) return;
    setSsoChecking(true);
    try {
      const { data } = await axios.get<SsoCheckResult>(
        `${API_URL}/sso/login?email=${encodeURIComponent(emailValue)}`,
      );
      setSsoResult(data);
      setSsoChecked(true);
    } catch {
      setSsoResult(null);
      setSsoChecked(true);
    } finally {
      setSsoChecking(false);
    }
  }, []);

  // Leave-veil — a soft light overlay that fades up on a successful
  // login so the dark auth surface brightens into the workspace rather
  // than flashing. Stays mounted because AuthGate unmounts LoginPage
  // the instant isAuthenticated flips — the workspace's own entrance
  // picks the visual up on the other side.
  const [leaving, setLeaving] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password);
      // If the backend asked for MFA, leave the page in place so the
      // MFA challenge form can render. Setting `leaving=true` would
      // fade the veil up over the MFA form and stall the user.
      if (result.requiresMfa) {
        return;
      }
      // Backend says the user must reset password BUT didn't supply a
      // token (EmailConfirmed=false branch — typical new-tenant signup
      // where the welcome email carries the real reset link). Show the
      // message inline rather than redirecting to a broken
      // /reset-password page with an empty token.
      if (result.checkEmailMessage) {
        setCheckEmailMessage(result.checkEmailMessage);
        return;
      }
      // Soft veil before the AuthGate swap — gives the transition
      // ~250ms of visible fade-up so the page brightens rather than
      // cuts.
      setLeaving(true);
      await new Promise((r) => setTimeout(r, 250));
    } catch (err: any) {
      const data = err.response?.data;
      if (data?.ssoEnforced) {
        setError('Your organisation requires SSO login. Use the SSO button below.');
      } else {
        setError(data?.error || 'Login failed. Please try again.');
      }
      setLeaving(false);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (ssoChecked) {
      setSsoChecked(false);
      setSsoResult(null);
    }
  };

  const handleSendMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    const targetEmail = magicLinkEmail || email;
    if (!targetEmail) return;
    setError('');
    setMagicLinkLoading(true);
    try {
      // Stash now so that if the user clicks the email link in this same
      // browser session (sessionStorage is per-tab but per-origin),
      // /auth/callback returns them to /oauth/desktop?session=… afterward.
      stashReturnUrlIfAuthHandoff();
      await axios.post(`${API_URL}/auth/magic-link`, { email: targetEmail });
      setMagicLinkSent(true);
    } catch {
      // API always returns 200 to prevent enumeration; only network errors land here.
      setError('Unable to send magic link. Please try again.');
    } finally {
      setMagicLinkLoading(false);
    }
  };

  const handlePasskeyLogin = async () => {
    setError('');
    setPasskeyLoading(true);
    try {
      const options = await passkeysApi.loginOptions(email || undefined);
      const challengeKey = options.challenge;
      const assertion = await startAuthentication({ optionsJSON: options });
      const result = await passkeysApi.login({ response: assertion, challengeKey });

      // Soft veil before the AuthGate swap — same treatment as the
      // password path so successful passkey login doesn't flash
      // dark → bright → dark. Fades up over 520ms then applyTokens
      // hands off to the workspace's own entrance.
      setLeaving(true);
      await new Promise((r) => setTimeout(r, 250));

      // Apply tokens through React state so OAuthAuthorizePage keeps its query
      // string. Writing sessionStorage + window.location.reload() was unreliable
      // on the authorize flow — some browsers dropped the search params.
      applyTokens(result);
      // Preserve the URL for any auth-handoff flow (OAuth-for-MCP consent
      // and the Mac desktop sign-in handoff). Passkey is itself a strong
      // factor so both flows can complete on passkey alone — no MFA hop.
      const onAuthHandoffFlow =
        window.location.pathname.startsWith('/oauth/authorize')
        || window.location.pathname.startsWith('/oauth/desktop');
      if (!onAuthHandoffFlow && window.location.pathname !== '/') {
        window.history.replaceState(null, '', '/');
      }
    } catch (err: any) {
      if (err.name === 'NotAllowedError') {
        setError('Passkey authentication was cancelled.');
      } else if (err.response?.status === 401) {
        // The browser sent a credential Cadence doesn't recognise (e.g. a
        // passkey saved in a third-party manager that was never registered
        // here). Point the user at a path forward instead of a bare failure.
        setError('This passkey isn’t registered with Cadence. Use a different sign-in method, or register this passkey in Settings → Security.');
      } else {
        setError(err.response?.data?.error || 'Passkey authentication failed. Please try again.');
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  const showSsoButton = ssoChecked && ssoResult?.ssoEnabled;

  // When this login page is rendered inside a native desktop-handoff
  // (/oauth/desktop) — e.g. the in-app browser of the Mac/iOS apps — thread the
  // handoff URL to the backend as a returnUrl query param. The server echoes it
  // back on the OAuth success redirect, so the cadence:// handoff survives even
  // if the in-app browser drops the sessionStorage stash. No-op on the normal
  // web login (returns null → empty suffix).
  const handoffReturn = currentAuthHandoffReturnUrl();
  const oauthReturnSuffix = handoffReturn
    ? `?returnUrl=${encodeURIComponent(handoffReturn)}`
    : '';

  // Hide all third-party social sign-in (Microsoft / Apple) when this page is
  // rendered inside the NATIVE app's in-app browser (the /oauth/desktop
  // handoff used by the iOS + Mac apps). Two reasons, both App Store rejections:
  // (1) social sign-in only works for an already-linked account, so a reviewer
  // using their own Apple/Microsoft ID gets "no account" and is bounced back to
  // the login page — read as a bug (Guideline 2.1a). (2) Offering Microsoft but
  // not Apple (or vice-versa) trips Guideline 4.8; offering NEITHER in the app
  // sidesteps 4.8 entirely. The reviewer (and real users) sign in here with
  // email/password, passkey, or magic-link. The normal WEB login (/) is
  // unaffected and keeps Microsoft for real users with linked accounts.
  const isNativeAppSignIn = window.location.pathname.startsWith('/oauth/desktop');

  return (
    <div className="dark relative flex min-h-screen items-center justify-center overflow-x-hidden overflow-y-auto bg-[#070A12] py-6 sm:py-0">
      {/* `overflow-y-auto` (not `overflow-hidden`) so the form can scroll
          into view when the iOS keyboard pushes the viewport up — without
          this the iOS app's ASWebAuthenticationSession sheet renders the
          heading but clips the email/password fields below the keyboard. */}
      {/* Ambient backdrop layer (z=0): warm copper halo anchored
          bottom-centre + cool blue top-glow + top/bottom vignette per
          the spec. No multi-stop gradient, no violet/teal orbs. */}
      <div aria-hidden className="pointer-events-none absolute inset-0 z-0">
        <div
          className="absolute left-1/2 bottom-[-24%] h-[78vh] w-[120vw] -translate-x-1/2 rounded-full blur-[14px]"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(255,176,122,0.14) 0%, transparent 62%)',
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(120% 90% at 50% -10%, rgba(125,177,255,0.05), transparent 55%), linear-gradient(180deg, rgba(0,0,0,0.28), transparent 32%, transparent 68%, rgba(0,0,0,0.45))',
          }}
        />
      </div>

      {/* Grain texture layer (z=1): SVG fractalNoise, fixed inset, mix
          blended over the ambient layer for the film-grain quality. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-[1]"
        style={{
          backgroundImage: `url("${grainSvg}")`,
          backgroundSize: '160px 160px',
          opacity: 0.04,
          mixBlendMode: 'overlay',
        }}
      />

      {/* Leave-veil (z=60): fades up on successful login. Targets the
          dark slate the Brief lands on so the auth → Brief transition
          is dark → dark rather than dark → bright → dark. 520ms target
          matches the post-login nav timing per spec. */}
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-0 z-[60] transition-opacity duration-[520ms] ease-out',
          leaving ? 'opacity-100' : 'opacity-0',
        )}
        style={{ background: '#070A12' }}
      />

      {/* Form column (z=2). Sits directly on the backdrop — no card
          chrome, no border, no backdrop-blur. The page is the moment;
          the form is just the words. 32px page padding per spec.
          Each `.rise` element animates up from translateY(16px) +
          opacity 0 on its own `--d` delay once `ready` flips, giving
          the surface the same staggered entrance the Brief uses. */}
      <div className="relative z-[2] mx-4 flex w-full max-w-[392px] flex-col p-[32px]">
        <div
          className={cn('rise mb-5', ready && 'is-revealed')}
          style={{ '--d': '120ms' } as React.CSSProperties}
        >
          <BrandMark size="md" showStratiumLine inlineStratiumTagline accent="light" />
        </div>

        <h1
          className={cn(
            // Shrunk + tightened on mobile so the form clears the iOS
            // keyboard on iPhone-portrait — the 33px desktop heading
            // pushes the email field below the fold inside the
            // ASWebAuthenticationSession sheet. sm: breakpoint restores
            // the full editorial scale at iPad / desktop widths.
            'rise mb-2 font-serif text-[22px] font-light leading-[1.15] tracking-[-0.01em] text-white sm:mb-[10px] sm:text-[33px] sm:leading-[1.12]',
            ready && 'is-revealed',
          )}
          style={{ '--d': '260ms' } as React.CSSProperties}
        >
          The operating system<br />for executive teams.
        </h1>
        <p
          className={cn(
            'rise mb-5 text-[14px] font-light tracking-wide text-[rgba(234,238,246,0.62)] sm:mb-[30px] sm:text-[15px]',
            ready && 'is-revealed',
          )}
          style={{ '--d': '380ms' } as React.CSSProperties}
        >
          Sign in to your day.
        </p>

        {error && (
          <Alert variant="error" className="mb-3 rounded-xl">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {checkEmailMessage && !error && (
          <Alert variant="info" className="mb-3 rounded-xl border-[rgba(125,177,255,0.30)] bg-[rgba(125,177,255,0.08)]">
            <AlertDescription className="text-[rgba(234,238,246,0.85)]">{checkEmailMessage}</AlertDescription>
          </Alert>
        )}

        {!requiresMfa ? (
          <>
            <form
              onSubmit={handleLogin}
              className={cn('rise flex flex-col gap-[14px]', ready && 'is-revealed')}
              style={{ '--d': '520ms' } as React.CSSProperties}
            >
              <div className="flex flex-col gap-[7px]">
                <Label htmlFor="login-email" className="text-[12.5px] tracking-[0.02em] text-[rgba(234,238,246,0.40)]">
                  Work email
                </Label>
                <Input
                  id="login-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  onBlur={() => { if (email && !ssoChecked) checkSso(email); }}
                  autoFocus
                  autoComplete="username webauthn"
                  className={FIELD_CLASS}
                />
              </div>

              {/* Password field rendered from first paint — the spec is a
                  single Sign in submit, not a two-stage Continue→Sign in.
                  SSO detection still runs on email blur, but it's an
                  informational hint, not a gate on the password field. */}
              <div className="flex flex-col gap-[7px]">
                <Label htmlFor="login-password" className="text-[12.5px] tracking-[0.02em] text-[rgba(234,238,246,0.40)]">
                  Password
                </Label>
                <Input
                  id="login-password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  className={FIELD_CLASS}
                />
              </div>
              <div className="text-right">
                <a
                  href="/forgot-password"
                  className="text-xs text-[rgba(136,146,168,0.8)] hover:text-[#7DB1FF] hover:underline"
                >
                  Forgot password?
                </a>
              </div>
              <Button
                type="submit"
                disabled={loading || !email || !password}
                className={cn('mt-[6px] h-12 w-full text-[15px] font-semibold', PRIMARY_BUTTON)}
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Sign in'}
              </Button>

              {ssoChecking && (
                <div className="my-3 flex justify-center">
                  <Loader2 className="h-5 w-5 animate-spin text-[rgba(136,146,168,0.6)]" />
                </div>
              )}

              {/* Inline SSO button — surfaces when the domain has an SSO
                  provider configured. Optional alternative to the password
                  submit above; not a gate. */}
              {showSsoButton && (
                <Button
                  type="button"
                  className={cn('mt-3 h-12 w-full text-[15px] font-semibold normal-case', SSO_BUTTON)}
                  asChild
                >
                  <a
                    href={ssoResult?.loginUrl || '#'}
                    onClick={() => stashReturnUrlIfAuthHandoff()}
                  >
                    {ssoResult?.displayName || `Sign in with ${ssoResult?.provider || 'SSO'}`}
                  </a>
                </Button>
              )}

              {/* Enforce-SSO advisory — shown after the SSO check confirms
                  the org is SSO-only. The password submit will fail with a
                  matching error; the advisory just sets expectations. */}
              {ssoResult?.ssoEnabled && ssoResult.enforceSso && (
                <p className="mt-2 text-center text-xs text-[rgba(136,146,168,0.8)]">
                  Your organisation requires SSO authentication.
                </p>
              )}
            </form>

            {/* OAuth + passkey + magic-link options — rendered alongside
                the password form rather than gated behind the previous
                two-stage Continue→Sign in flow. Microsoft / Google /
                passkey / email-link are intentionally kept (per current
                product scope) even though the redesign spec calls for
                Microsoft only. Wrapped in a single .rise slot so the
                cluster reveals together at 760ms. */}
            <div
              className={cn('rise', ready && 'is-revealed')}
              style={{ '--d': '760ms' } as React.CSSProperties}
            >
              <Divider label="or" />

              {/* Third-party social sign-in (Microsoft / Apple) is hidden in the
                  native app's in-app browser (isNativeAppSignIn) — see the note
                  by its definition above. Shown only on the normal web login. */}
              {!isNativeAppSignIn && (
                <>
                  <Button
                    asChild
                    className={cn('mb-2', OAUTH_BUTTON)}
                    variant="outline"
                    onClick={() => { stashReturnUrlIfAuthHandoff(); setLeaving(true); }}
                  >
                    <a href={`${API_URL}/auth/login/microsoft${oauthReturnSuffix}`}>
                      Continue with Microsoft
                    </a>
                  </Button>

                  {/* "Sign in with Google" is intentionally NOT rendered: Google
                      OAuth is not configured in production (no Authentication:Google
                      settings on cadence-prod-api), so the button would dead-end —
                      this was the App Store "error page on Sign in with Google"
                      rejection. To restore: configure a Google OAuth client (redirect
                      https://api.stratiumhq.com/api/auth/callback/Google) + the Azure
                      app settings, then re-add the button:
                      <a href={`${API_URL}/auth/login/google${oauthReturnSuffix}`}>Sign in with Google</a>
                      Backlogged in BACKLOG.md. */}

                  {/* Sign in with Apple — required by Guideline 4.8 wherever
                      third-party social sign-in (Microsoft above) is offered.
                      Hidden in the native app (no social there → 4.8 N/A). */}
                  <Button
                    asChild
                    className={OAUTH_BUTTON}
                    variant="outline"
                    onClick={() => { stashReturnUrlIfAuthHandoff(); setLeaving(true); }}
                  >
                    <a href={`${API_URL}/auth/login/apple${oauthReturnSuffix}`}>
                      Sign in with Apple
                    </a>
                  </Button>
                </>
              )}

              <Button
                variant="outline"
                onClick={handlePasskeyLogin}
                disabled={passkeyLoading}
                className={cn('mt-2 gap-2', PASSKEY_BUTTON)}
              >
                {passkeyLoading ? <Loader2 className="h-4 w-4 animate-spin text-[#7DB1FF]" /> : <Fingerprint className="h-4 w-4" />}
                Sign in with passkey
              </Button>

              {!showMagicLink && (
                <Button
                  variant="ghost"
                  onClick={() => { setShowMagicLink(true); setMagicLinkEmail(email); }}
                  className="mt-2 h-10 w-full text-[13px] font-medium normal-case text-[rgba(136,146,168,0.8)] hover:bg-[rgba(125,177,255,0.06)] hover:text-[#7DB1FF]"
                >
                  Sign in with email link
                </Button>
              )}
            </div>

            {showMagicLink && !magicLinkSent && (
              <div className="mt-3">
                <Divider label="email link" />
                <form onSubmit={handleSendMagicLink}>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="ml-email" className="text-[rgba(136,146,168,0.8)]">Email for magic link</Label>
                    <Input
                      id="ml-email"
                      type="email"
                      required
                      value={magicLinkEmail}
                      onChange={(e) => setMagicLinkEmail(e.target.value)}
                      className={FIELD_CLASS}
                    />
                  </div>
                  <Button
                    type="submit"
                    variant="outline"
                    disabled={magicLinkLoading || !magicLinkEmail}
                    className={cn('mt-3 gap-2', PASSKEY_BUTTON)}
                  >
                    {magicLinkLoading ? <Loader2 className="h-4 w-4 animate-spin text-[#7DB1FF]" /> : null}
                    Send magic link
                  </Button>
                </form>
              </div>
            )}

            {magicLinkSent && (
              <Alert variant="success" className="mt-3 rounded-xl">
                <AlertDescription>
                  Check your email for a login link. It expires in 15 minutes.
                </AlertDescription>
              </Alert>
            )}
          </>
        ) : (
          // Second factor — extracted into its own component (shares the
          // AuthProvider MFA context, so verifyMfa completes the in-flight
          // challenge). Kept as a component, not a route, so the in-memory
          // mfaToken survives.
          <MfaChallengePage />
        )}

        {/* Footer row — per the spec: "Forgot password · 14 day free
            trial" instead of the previous "Don't have an account? Start
            free trial" violet link. The forgot-password CTA up by the
            password field is the actual destination; this is just the
            footer affordance line. Slots at the tail of the .rise
            ladder (1000ms) so the surface lands top-to-bottom. */}
        <div
          className={cn(
            'rise mt-6 flex items-center justify-center gap-3 text-[12px] text-[rgba(234,238,246,0.62)]',
            ready && 'is-revealed',
          )}
          style={{ '--d': '1000ms' } as React.CSSProperties}
        >
          <a href="/forgot-password" className="hover:text-white">
            Forgot password
          </a>
          <span aria-hidden className="text-[rgba(234,238,246,0.30)]">·</span>
          <a href="/signup" className="hover:text-white">
            14 day free trial
          </a>
        </div>

        {/* Stratium provenance — quiet anchor line at the bottom of the
            auth surface, mirroring Signup. */}
        <p
          className={cn(
            'rise mt-8 text-center text-[11px] tracking-wide text-[rgba(234,238,246,0.30)]',
            ready && 'is-revealed',
          )}
          style={{ '--d': '1200ms' } as React.CSSProperties}
        >
          Cadence is the product. Stratium is the company behind it.
        </p>
      </div>
    </div>
  );
}
